import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Smartphone, Share, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import useInstallPrompt from "@/hooks/useInstallPrompt";

/**
 * Permanent "Install app" / "Download app" button.
 * - Android/Desktop Chrome: fires the captured beforeinstallprompt (native install dialog)
 * - iOS Safari: opens a modal with visual instructions to "Add to Home Screen"
 * - Fallback (Firefox / already installed / SW blocked): opens the modal with a generic hint
 * Placement: in the Navbar next to "Share app" and inside the Home hero as a secondary CTA.
 */
export default function InstallAppButton({ variant = "nav", className = "" }) {
  const { canInstall, isIOS, isIOSNonSafari, isStandalone, promptInstall } = useInstallPrompt();
  const [open, setOpen] = useState(false);

  if (isStandalone) return null; // already installed — hide

  const handleClick = async () => {
    if (canInstall) {
      const outcome = await promptInstall();
      if (outcome === "accepted") {
        toast.success("HostelNow installed! Look for it on your home screen.");
        return;
      }
      if (outcome === "dismissed") return; // silent
    }
    // Either iOS or no captured prompt — show instructions
    setOpen(true);
  };

  const base = "gap-1.5 rounded-full font-semibold transition-colors";
  const styles =
    variant === "nav"
      ? "border-orange-200 text-orange-800 hover:bg-orange-50 hover:text-orange-900"
      : variant === "hero"
      ? "bg-orange-800 hover:bg-orange-900 text-white h-12 px-6"
      : "bg-white/90 border border-orange-300 text-orange-800 hover:bg-white";

  return (
    <>
      <Button
        data-testid="install-app-btn"
        onClick={handleClick}
        variant={variant === "nav" ? "outline" : "default"}
        className={`${base} ${styles} ${className}`}
      >
        <Download size={14} /> Install app
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="install-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Smartphone className="text-orange-800" /> Install HostelNow
            </DialogTitle>
          </DialogHeader>

          <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 flex items-start gap-3">
            <img src="/icon-192.png" className="w-14 h-14 rounded-xl flex-shrink-0" alt="" />
            <div className="text-sm text-slate-700">
              Add HostelNow to your home screen for one-tap booking, faster loading, and instant updates from your hostel owner.
            </div>
          </div>

          {isIOS ? (
            <div className="mt-4 space-y-3">
              <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">iPhone / iPad · Safari</div>
              {isIOSNonSafari && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                  <b>Open this page in Safari first.</b> Installing to the home screen only works from Safari on iPhone / iPad. Tap the <span className="font-mono">⋯</span> or Share menu of your current browser and choose <b>“Open in Safari”</b>, then come back here.
                </div>
              )}
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-orange-800 text-white grid place-items-center text-xs font-bold flex-shrink-0">1</span>
                  <div className="text-sm">Tap the <Share size={14} className="inline text-sky-600" /> <span className="font-semibold">Share</span> icon in Safari (bottom of the screen on iPhone, top-right on iPad).</div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-orange-800 text-white grid place-items-center text-xs font-bold flex-shrink-0">2</span>
                  <div className="text-sm">Scroll down and tap <Plus size={14} className="inline" /> <span className="font-semibold">Add to Home Screen</span>.</div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-orange-800 text-white grid place-items-center text-xs font-bold flex-shrink-0">3</span>
                  <div className="text-sm">Tap <span className="font-semibold">Add</span> in the top-right — done!</div>
                </li>
              </ol>
              <p className="text-[11px] text-slate-500 mt-2">Note: On iOS, the "Add to Home Screen" option only appears in <strong>Safari</strong>, not Chrome or other browsers.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">Android · Chrome</div>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-orange-800 text-white grid place-items-center text-xs font-bold flex-shrink-0">1</span>
                  <div className="text-sm">Tap the <span className="font-mono px-1.5 py-0.5 bg-slate-100 rounded text-xs font-semibold">⋮</span> menu at the top-right of Chrome.</div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-orange-800 text-white grid place-items-center text-xs font-bold flex-shrink-0">2</span>
                  <div className="text-sm">Tap <span className="font-semibold">Install app</span> (or <span className="font-semibold">Add to Home Screen</span>).</div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-orange-800 text-white grid place-items-center text-xs font-bold flex-shrink-0">3</span>
                  <div className="text-sm">Confirm <span className="font-semibold">Install</span> — HostelNow appears on your home screen.</div>
                </li>
              </ol>
              <div className="mt-3 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-2">
                <CheckCircle2 size={14} /> On desktop Chrome, look for the <Download size={14} className="inline" /> icon at the right side of the address bar.
              </div>
            </div>
          )}

          <Button
            data-testid="install-modal-close"
            onClick={() => setOpen(false)}
            variant="outline"
            className="w-full rounded-full h-11 mt-4"
          >
            Got it
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
